import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { equals } from 'ramda';
import { Form, Formik, FormikProps, FormikErrors } from 'formik';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { setUpdateProfileErrorRoutine, updateProfileRoutine } from '../../routines';
import { updateProfileValidationSchema } from '../../../../common/validation/yup';
import RegularButton from '../../../../components/RegularButton';
import CustomPopover from '../../../../components/CustomPopover';
import TeamList from '../../../../containers/TeamList';
import { IUpdateProfileInfo } from '../../../../common/models/user/IUpdateProfileInfo';
import { IUserProfileData } from '../../../../common/models/user/IUserProfileData';
import styles from './styles.module.scss';
import { FormattedMessage } from 'react-intl';
import Icon from '../../../../components/Icon';
import Input from '../../../../components/Input';
import { ITeam } from '../../../../common/models/team/ITeam';

interface IProps {
  resetSaveError: IBindingCallback1<null>;
  defaultUserData: IUserProfileData;
  updateProfile: IBindingCallback1<IUpdateProfileInfo>;
}

const ProfileForm: FunctionComponent<IProps> = ({
  resetSaveError,
  defaultUserData,
  updateProfile
}) => {
  const TeamListComponent = useCallback(({ hidePopover, value, onChange }) => (
    <TeamList
      selectedIds={value ? [value] : undefined}
      onSelect={([data]) => {
        onChange(data);
        if (hidePopover) {
          hidePopover();
        }
      }}
    />
  ), []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSaveClick = (submit: IBindingAction) => {
    setIsSubmitting(true);
    submit();
  };

  const getError = (fieldName: keyof IUserProfileData, errors: FormikErrors<IUserProfileData>) => (
    isSubmitting ? errors[fieldName] : undefined
  ) as string | undefined;

  const isEqual = (values: IUserProfileData) => equals(values, defaultUserData);

  useEffect(() => () => resetSaveError(null), []);

  const onSubmit = ({ team, ...values }: typeof defaultUserData) => {
    const data: IUpdateProfileInfo = {
      teamId: team?.id as string,
      ...values
    };
    updateProfile(data);
  };

  return (
    <Formik
      validationSchema={updateProfileValidationSchema}
      initialValues={defaultUserData}
      onSubmit={onSubmit}
      validateOnMount
    >
      {({
        isValid,
        submitForm,
        values,
        setFieldValue,
        errors
      }: FormikProps<IUserProfileData>) => (
        <Form>
          <Input
            label={{ id: 'firstName', defaultMessage: 'First Name' }}
            errorIntl={getError('firstName', errors)}
            onChange={val => setFieldValue('firstName', val)}
            variant="underlined"
            value={values.firstName}
            className={styles.profileInput}
          />
          <Input
            label={{ id: 'lastName', defaultMessage: 'Last Name' }}
            errorIntl={getError('lastName', errors)}
            onChange={val => setFieldValue('lastName', val)}
            variant="underlined"
            value={values.lastName}
            className={styles.profileInput}
          />
          <Input
            label={{ id: 'myInitials', defaultMessage: 'My initials (for avatar circle)' }}
            onChange={val => setFieldValue('initials', val)}
            variant="underlined"
            errorIntl={getError('initials', errors)}
            value={values.initials}
            className={styles.profileInput}
          />
          <Input
            label={{ id: 'phoneNumber', defaultMessage: 'Phone number' }}
            onChange={val => setFieldValue('phoneNumber', val)}
            variant="underlined"
            errorIntl={getError('phoneNumber', errors)}
            value={values.phoneNumber}
            type="phoneNumber"
            className={styles.profileInput}
            dropdownPosition="top"
          />
          <div className={`popup-wrapper ${styles.teamDropdownWrp}`}>
            <span className={`${styles.teamLabel} t-14`}>
              <FormattedMessage id="profilePane.myTeam" defaultMessage="My team" />
            </span>
            <CustomPopover
              placement="top-end"
              popoverClassName={styles.chooseTeamPopover}
              trigger={(
                <div className="text-dropdown-trigger">
                  <span className="overflow-ellipsis">
                    {values.team.name}
                  </span>
                  <Icon icon="chevronDown" className="popup-chevron text-dropdown-trigger-chevron" />
                </div>
              )}
              rootClose
            >
              <TeamListComponent onChange={(val: ITeam) => setFieldValue('team', val)} value={values.team?.id} />
            </CustomPopover>
          </div>
          <div className={`mt-4 ${styles.saveButtonWrp} d-flex justify-content-end`}>
            <RegularButton
              enableAction={!isEqual(values)}
              onClick={() => onSaveClick(submitForm)}
              textIntlId="saveChanges"
              defaultMessage="Save changes"
              disabled={!isValid || isEqual(values)}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

const mapDispachToProps = {
  resetSaveError: setUpdateProfileErrorRoutine,
  updateProfile: updateProfileRoutine
};

export default connect(null, mapDispachToProps)(ProfileForm);
